/**
 * @fileoverview This service is responsible for managing the application's theme.
 */

import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, Observable, take, tap } from 'rxjs';

import { selectCompanyTheme } from '@public-page/store/company';

import { environment } from '@environments';

/**
 * @class ThemeService
 * @description A service that manages the application's theme.
 */
@Injectable()
export class ThemeService {
  /**
   * @private
   * @type {string}
   * @description The class name for the link element that will be created to apply the theme.
   */
  private linkElementClassName: string = 'app-theme';

  /**
   * @private
   * @type {Observable<string | undefined>}
   * @description An observable that emits the current theme.
   */
  private theme$: Observable<string | undefined> = this.store.select(selectCompanyTheme);
  constructor(private store: Store) {}

  /**
   * @public
   * @method injectTheme
   * @description Injects the current theme into the application by creating a link element and appending it to the document head.
   * @returns {Observable} An observable that completes after the theme has been injected.
   */
  public injectTheme(): Observable<string | undefined> {
    return this.theme$.pipe(
      filter((val) => val !== undefined),
      tap((themeName) => this.setStyle(themeName)),
      take(1)
    );
  }

  /**
   * @private
   * @method setStyle
   * @description Sets the application's theme by creating a link element and appending it to the document head.
   * @param {string} theme - The name of the theme to be applied. Defaults to 'theme-1'.
   */
  private setStyle(theme: string = 'theme-1') {
    // if change location of theme file on dist check href
    const href = `${environment.deployURL}${theme}.css`;
    this.createLinkElement(href);
  }

  /**
   * @private
   * @method createLinkElement
   * @description Creates a link element with the specified href and appends it to the document head.
   * @param {string} href - The href for the link element.
   * @returns {HTMLLinkElement} The created link element.
   */
  private createLinkElement(href: string) {
    const linkEl = document.createElement('link');
    linkEl.setAttribute('rel', 'stylesheet');
    linkEl.setAttribute('href', href);
    linkEl.classList.add(this.linkElementClassName);
    document.head.appendChild(linkEl);
    return linkEl;
  }
}
